import React, { useState, useEffect } from 'react';
import {
  Stack,
  YStack,
  XStack,
  SizableText,
  Separator,
  useTheme,
  ScrollView,
} from 'tamagui';
import { Link } from 'solito/link';
import { Bookmark, StarButton, ColorButton } from 'components/utility/Buttons';
import Loading from 'components/utility/Loading';
import { Flex, Center } from 'components/base/General';
import { DetailHeader } from 'app/components/base/Detail';
import {
  useGetCollectionWithArticlesQuery,
  useCreateOrUpdateBookmarkMutation,
  useDeleteBookmarkMutation,
  useCreateOrUpdateReadStatusMutation,
  useDeleteReadStatusMutation,
} from 'utils/RTKGraphQL';
import { useSelector } from 'react-redux';
import Breadcrumb from 'app/components/utility/Breadcrumb';

interface Article {
  id: string;
  title: string;
  slug: string;
  read: boolean;
  perLanguage: {
    premium: number;
  }[];
}

interface ArticleItemProps {
  article: Article;
  profileId: string | undefined;
}

const ArticleItem = ({ article, profileId }: ArticleItemProps) => {
  const [read, setRead] = useState(article.read);
  const theme = useTheme();

  const [createOrUpdateReadStatus] = useCreateOrUpdateReadStatusMutation();
  const [deleteReadStatus] = useDeleteReadStatusMutation();

  const readArticleToggle = async () => {
    if (!profileId) return;

    try {
      if (read) {
        await deleteReadStatus({
          userId: profileId,
          articleId: article.id,
        }).unwrap();
      } else {
        await createOrUpdateReadStatus({
          userId: profileId,
          articleId: article.id,
        }).unwrap();
      }
      setRead(!read);
    } catch (error) {
      console.error('Error toggling read status:', error);
    }
  };

  const getArticleStatus = (): string | null => {
    if (article.perLanguage[0]?.premium > 1) {
      return 'Premium';
    } else if (article.perLanguage[0]?.premium === 1) {
      return 'Login';
    }
    return null;
  };

  const articleStatus = getArticleStatus();

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth={1}
      borderBottomColor="$gray400"
      backgroundColor={'$warmgray200'}
      $base={{
        maxWidth: '100%',
        gap: 10,
      }}
      $sm={{
        maxWidth: '95%',
        width: '95%',
      }}
      $md={{
        width: '700px',
      }}
      $lg={{ width: '900px' }}
      $xl={{
        width: '1100px',
      }}
      p={15}
    >
      <YStack
        flexShrink={1}
        flexGrow={1}
        $md={{ flexDirection: 'row' }}
        alignItems="flex-start"
        gap={20}
      >
        <SizableText flexShrink={1} flexWrap="wrap">
          {article.title}
        </SizableText>
        {articleStatus && (
          <SizableText
            fontSize="$xs"
            color={articleStatus === 'Premium' ? '$red500' : 'black'}
            fontWeight="bold"
          >
            {articleStatus}
          </SizableText>
        )}
      </YStack>
      <XStack
        justifyContent="flex-end"
        alignItems="center"
        gap={20}
        flexShrink={0}
      >
        <Link href={`/article/${article.slug}`}>
          <ColorButton width={100} title="Read" />
        </Link>
        <StarButton saved={read} onPress={readArticleToggle} />
      </XStack>
    </XStack>
  );
};

interface CollectionPageProps {
  slug: string;
}

const CollectionPage = ({ slug }: CollectionPageProps) => {
  const theme = useTheme();
  const profile = useSelector((state: any) => state.profile);
  const [bookmark, setBookmark] = useState(false);

  const { data, isLoading, error } = useGetCollectionWithArticlesQuery(
    { slug, userId: profile?.id },
    { skip: !slug }
  );

  const [createOrUpdateBookmark, { isLoading: isCreatingBookmark }] =
    useCreateOrUpdateBookmarkMutation();
  const [deleteBookmark, { isLoading: isDeletingBookmark }] =
    useDeleteBookmarkMutation();

  const bookmarkToggle = async () => {
    if (!profile) return;

    try {
      if (bookmark) {
        await deleteBookmark({
          userId: profile.id,
          collectionId: data.id,
        }).unwrap();
      } else {
        await createOrUpdateBookmark({
          userId: profile.id,
          collectionId: data.id,
        }).unwrap();
      }
      setBookmark(!bookmark);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  useEffect(() => {
    if (data) {
      setBookmark(data.bookmarked);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Flex
        flexGrow={1}
        bg={theme.background}
        alignSelf="center"
        justifyContent="center"
        width="100%"
        minHeight={500}
      >
        <Loading />
      </Flex>
    );
  }

  if (
    error ||
    !data ||
    !data.collectionPerLanguage ||
    data.collectionPerLanguage.length === 0
  ) {
    return (
      <Center>
        <SizableText>Unable to load collection data.</SizableText>
      </Center>
    );
  }

  return (
    <ScrollView bg={theme.background}>
      <Center>
        <DetailHeader
          article={data}
          perLanguage={data.collectionPerLanguage[0]}
        />

        <Stack
          mt={60}
          py={20}
          mx={20}
          alignItems="center"
          justifyContent="space-around"
          borderColor="transparent"
          borderWidth={1}
          display="flex"
          gap={20}
          $base={{
            maxWidth: '95%',
            flexDirection: 'column',
          }}
          $sm={{
            flexDirection: 'column',
          }}
          $md={{
            borderColor: 'black',
            flexDirection: 'row',
            gap: 0,
            maxWidth: 1100,
          }}
        >
          <Stack
            $base={{ width: '95%' }}
            $md={{
              width: '80%',
            }}
          >
            <SizableText>
              {data.collectionPerLanguage[0].description}
            </SizableText>
          </Stack>
          {profile && (
            <Bookmark
              saved={bookmark}
              onPress={bookmarkToggle}
              size={80}
              color="$pred500"
              disabled={isCreatingBookmark || isDeletingBookmark}
            />
          )}
        </Stack>
        <Separator />

        <Stack py={50}>
          <Breadcrumb skipSegments={['collection']} />
          <YStack gap={20} alignItems="center">
            {data.articles.map((article: Article) => (
              <ArticleItem
                key={article.id}
                article={article}
                profileId={profile?.id}
              />
            ))}
          </YStack>
        </Stack>
      </Center>
    </ScrollView>
  );
};

export default CollectionPage;
